import { ThunkDispatch } from "core-integration/src/store";
import { chipActivationHandle } from "core-integration/src/store/chips/thunks";
import { IPotentialChip } from "core-integration/src/store/chips/types";
import { getTransferState } from "core-integration/src/store/squad/reducers";
import { Button } from "plos/src/components/buttons/Button";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getChipName } from "../../../utils/chips";
import { activeTransferButton, buttonWrap } from "./chipSheet.css";

interface TransfersChipActionProps {
  chip: IPotentialChip;
  handleClose: () => void;
  showModalConfirmation: boolean;
  setShowModalConfirmation: Dispatch<SetStateAction<boolean>>;
}

const TransfersChipAction = ({
  chip,
  handleClose,
  showModalConfirmation,
  setShowModalConfirmation,
}: TransfersChipActionProps) => {
  const { t } = useTranslation("chips");
  const dispatch = useDispatch<ThunkDispatch>();

  const transferState = useSelector(getTransferState);

  const activateChip = (name: string) => dispatch(chipActivationHandle(name));
  const deactivateChip = () => dispatch(chipActivationHandle(null));

  const handlePlayClick = (chipName: string, handleHide: () => void) => {
    let askConfirmation = false;
    if (chipName === "freehit") {
      askConfirmation = transferState ? transferState.made > 0 : false;
    } else if (chipName === "wildcard") {
      askConfirmation =
        transferState && transferState.limit ? transferState.made > 1 : false;
    }

    if (!askConfirmation) {
      activateChip(chipName);
      handleHide();
      setShowModalConfirmation(false);
    } else {
      setShowModalConfirmation(true);
    }
  };

  const handleConfirmClick = (chipName: string) => {
    setShowModalConfirmation(false);
    activateChip(chipName);
  };

  const handleCancelClick = () => {
    setShowModalConfirmation(false);
    deactivateChip();
  };

  const chipDisplayName = getChipName(t, chip.name);

  switch (chip.status_for_entry) {
    case "available":
      return (
        <div className={buttonWrap}>
          {showModalConfirmation ? (
            <>
              <Button
                appearance="success"
                size="small"
                fullWidth
                onPress={() => {
                  handleConfirmClick(chip.name);
                  handleClose();
                }}
              >
                {t("action.confirm", "Confirm {{chip}}", {
                  chip: chipDisplayName,
                })}
              </Button>
              <Button
                appearance="error"
                size="small"
                fullWidth
                onPress={() => {
                  handleCancelClick();
                  handleClose();
                }}
              >
                {t("action.cancel", "Cancel {{chip}}", {
                  chip: chipDisplayName,
                })}
              </Button>
            </>
          ) : (
            <Button
              appearance="success"
              size="small"
              fullWidth
              onPress={() => {
                handlePlayClick(chip.name, handleClose);
              }}
            >
              {t("action.play", "Play {{chip}}", {
                chip: chipDisplayName,
              })}
            </Button>
          )}
        </div>
      );

    case "unavailable":
      return null;

    case "active":
      if (chip.is_pending) {
        return (
          <Button
            appearance="error"
            size="small"
            fullWidth
            onPress={() => {
              handleClose();
              deactivateChip();
            }}
          >
            {t("action.cancel", "Cancel {{chip}}", {
              chip: chipDisplayName,
            })}
          </Button>
        );
      }
      // TODO: Remove activeTransferButton styles and replace appropriately
      return (
        <div className={activeTransferButton}>
          {t("label.active", "Active")}
        </div>
      );

    default:
      return null;
  }
};

export default TransfersChipAction;
